import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, Output, ViewChild } from '@angular/core';
import { MatTable, MatTableModule } from '@angular/material/table';
import { FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIcon } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatInput } from '@angular/material/input';
import { CommonModule } from '@angular/common';
import { GuestFormDialogComponent } from './guest-form-dialog/guest-form-dialog.component';
import { NewGuestExitDialogComponent } from '../../../components/new-guest-exit-dialog/new-guest-exit-dialog.component';
import { VerificationPackage } from '../../../../verification-packages/models/VerificationPackage';
import { ReservationGuest, ReservationGuestDisplayedColumns } from '../../../models/Reservation';
import { Country } from '../../../../../shared/address/Country';
import { State } from '../../../../../shared/address/State';

@Component({
  selector: 'app-guests-step',
  templateUrl: './guests-step.component.html',
  styleUrls: ['./guests-step.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatExpansionModule,
    MatIcon,
    MatFormFieldModule,
    MatTableModule,
    MatInput,
    MatButtonModule
  ]
})
export class GuestsStepComponent implements AfterViewInit, OnChanges {

  @Input('guestsForm') form!: FormGroup;
  @Input('verificationPackage') verificationPackage!: VerificationPackage;
  @Input('countries') countries!: Country[];
  @Input('states') states!: State[];
  @Input('existingReservationGuests') existingReservationGuests!: ReservationGuest[];
  @Input('existingReservationGuestCount') existingReservationGuestCount!: number;

  @Output() guestsAdded: EventEmitter<ReservationGuest[]> = new EventEmitter<ReservationGuest[]>();
  @Output() numberOfGuestsChanged: EventEmitter<String> = new EventEmitter<String>();
  @ViewChild(MatTable) table!: MatTable<any>;
  @ViewChild('numberOfGuests') numberOfGuestsInput!: ElementRef;

  title: string = "Guests";
  panelOpen: boolean = false;
  complete: boolean = false;
  error: boolean = false;
  icon: string = "expand_more";

  dataSource: ReservationGuest[] = [];
  public columnsProps: string[] = ReservationGuestDisplayedColumns.map((column: any) => column.column);
  addedGuests: any[] = [];
  existingGuest: any;
  editExistingGuest: boolean = false;
  hasInteractedWithForm = false;

  constructor(
    private readonly dialog: MatDialog, 
    private readonly cdr: ChangeDetectorRef
  ) { }

  ngAfterViewInit() {
    this.numberOfGuestsInput.nativeElement.value = this.existingReservationGuestCount;
    this.numberOfGuestsChanged.emit(this.existingReservationGuestCount?.toString() ?? 0);
    this.hasInteractedWithForm = this.dataSource.length > 0;
    this.cdr.detectChanges();
  }

  ngOnChanges() {
    if (this.existingReservationGuests) {
      this.existingReservationGuests.forEach(guest => {
        guest.verification_package = this.verificationPackage;
      });

      this.addedGuests = this.existingReservationGuests;
      this.dataSource = this.existingReservationGuests;
    }
  }

  openGuestFormDialog(guest: any, existingGuest: boolean) {
    const dialogRef = this.dialog.open(GuestFormDialogComponent, {
      width: '90%',
      disableClose: true,
      data: {
        verificationCost: this.verificationPackage?.price > 0 ? this.verificationPackage.price : 0,
        countries: this.countries,
        form: this.form,
        existingGuest: existingGuest,
        guest: guest,
        addedGuests: this.addedGuests
      }
    });
    dialogRef.afterClosed().subscribe(async result => {
      if (result) {
        if (this.editExistingGuest) {
          let existingGuestIndex = this.addedGuests.findIndex(result => result.first_name + ' ' + result.last_name === this.existingGuest.first_name + ' ' + this.existingGuest.last_name);
          
          if (existingGuestIndex === -1) {
            return;
          } else {
            this.addedGuests[existingGuestIndex] = result;
            this.guestsAdded.emit(this.addedGuests);
            this.existingGuest = false;
          }
        } else {
          this.addedGuests.push(result);
          this.guestsAdded.emit(this.addedGuests);
        }

        this.dataSource = this.addedGuests;
        this.editExistingGuest = false;
        this.table?.renderRows();
      }
    });
  }

  onAddGuestClick() {
    this.hasInteractedWithForm = true;
    this.editExistingGuest = false;
    this.openGuestFormDialog('', false);
  }

  onEditGuestClick(guest: any) {
    this.editExistingGuest = true;
    this.existingGuest = guest;
    this.openGuestFormDialog(guest, true);
  }

  onDeleteGuestClick(guest: any) {
    this.hasInteractedWithForm = true;
    this.addedGuests.splice(this.addedGuests.indexOf(guest), 1);
    this.table?.renderRows();
    this.guestsAdded.emit(this.addedGuests);
    this.editExistingGuest = false;
  }

  openConfirmDeleteGuestDialog(guest: any) {
    const dialogRef = this.dialog.open(NewGuestExitDialogComponent, { width: '500px', disableClose: true });
  
    dialogRef.afterClosed().subscribe(async (shouldDelete: boolean) => {
      if (shouldDelete) {
        this.onDeleteGuestClick(guest);
      }
    });
  }

  stepIsValid() {
    return this.hasInteractedWithForm && this.dataSource.length > 0;
  }

  stepIsInvalid() {
    return this.hasInteractedWithForm && this.dataSource.length == 0;
  }

  onNumberOfGuestsChange() {
    this.numberOfGuestsInput.nativeElement.value = Math.abs(parseInt(this.numberOfGuestsInput.nativeElement.value));
    this.numberOfGuestsChanged.emit(this.numberOfGuestsInput.nativeElement.value);
  }
}